import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  CogIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const SuperAdminDashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const queryClient = useQueryClient();

  // Récupération des statistiques globales
  const { data: stats, isLoading: statsLoading } = useQuery(
    ['super-admin-stats', timeRange],
    () => axios.get(`/api/super-admin/stats?range=${timeRange}`).then(res => res.data),
  );

  // Récupération des activités récentes
  const { data: activities, isLoading: activitiesLoading } = useQuery(
    'super-admin-activities',
    () => axios.get('/api/super-admin/activities').then(res => res.data)
  );

  // Récupération des alertes système
  const { data: alerts, isLoading: alertsLoading } = useQuery(
    'super-admin-alerts',
    () => axios.get('/api/super-admin/alerts').then(res => res.data)
  );

  const timeRanges = [
    { value: '1d', label: '24h' },
    { value: '7d', label: '7 jours' },
    { value: '30d', label: '30 jours' },
    { value: '90d', label: '3 mois' }
  ];

  const StatCard = ({ title, value, change, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change !== undefined && (
            <div className="flex items-center mt-2">
              {change >= 0 ? (
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(change)}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs période précédente</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const ActivityItem = ({ activity }) => (
    <div className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg">
      <div className={`p-2 rounded-full ${
        activity.type === 'user' ? 'bg-blue-100' :
        activity.type === 'project' ? 'bg-green-100' :
        activity.type === 'payment' ? 'bg-yellow-100' :
        'bg-gray-100'
      }`}>
        {activity.type === 'user' && <UserGroupIcon className="h-4 w-4 text-blue-600" />}
        {activity.type === 'project' && <DocumentTextIcon className="h-4 w-4 text-green-600" />}
        {activity.type === 'payment' && <CurrencyDollarIcon className="h-4 w-4 text-yellow-600" />}
        {activity.type === 'system' && <CogIcon className="h-4 w-4 text-gray-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {activity.description}
        </p>
        <p className="text-sm text-gray-500">
          {new Date(activity.timestamp).toLocaleString('fr-FR')}
        </p>
      </div>
    </div>
  );

  const AlertItem = ({ alert }) => (
    <div className={`p-4 rounded-lg border-l-4 ${
      alert.level === 'critical' ? 'bg-red-50 border-red-400' :
      alert.level === 'warning' ? 'bg-yellow-50 border-yellow-400' :
      'bg-blue-50 border-blue-400'
    }`}>
      <div className="flex items-center">
        {alert.level === 'critical' && <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />}
        {alert.level === 'warning' && <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />}
        {alert.level === 'info' && <CheckCircleIcon className="h-5 w-5 text-blue-400 mr-2" />}
        <div>
          <h4 className="text-sm font-medium text-gray-900">{alert.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(alert.timestamp).toLocaleString('fr-FR')}
          </p>
        </div>
      </div>
    </div>
  );

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Vue d'ensemble complète de la plateforme Innovactive 360°
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {timeRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => queryClient.invalidateQueries(['super-admin-stats', 'super-admin-activities', 'super-admin-alerts'])}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Actualiser
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Utilisateurs totaux"
            value={stats?.users?.total || 0}
            change={stats?.users?.growth}
            icon={UserGroupIcon}
            color="blue"
          />
          <StatCard
            title="Projets actifs"
            value={stats?.projects?.total || 0}
            change={stats?.projects?.growth}
            icon={DocumentTextIcon}
            color="green"
          />
          <StatCard
            title="Revenus (€)"
            value={stats?.revenue?.total || 0}
            change={stats?.revenue?.growth}
            icon={CurrencyDollarIcon}
            color="yellow"
          />
          <StatCard
            title="Pays actifs"
            value={stats?.countries?.total || 0}
            change={stats?.countries?.growth}
            icon={GlobeAltIcon}
            color="purple"
          />
        </div>

        {/* Graphiques et métriques détaillées */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Graphique des utilisateurs */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des utilisateurs</h3>
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                <p>Graphique en cours de développement</p>
              </div>
            </div>
          </div>

          {/* Graphique des revenus */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des revenus</h3>
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-2" />
                <p>Graphique en cours de développement</p>
              </div>
            </div>
          </div>
        </div>

        {/* Activités récentes et alertes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Activités récentes */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activités récentes</h3>
            </div>
            <div className="p-6">
              {activitiesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement...</p>
                </div>
              ) : activities?.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {activities.map((activity, index) => (
                    <ActivityItem key={index} activity={activity} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ClockIcon className="h-12 w-12 mx-auto mb-2" />
                  <p>Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>

          {/* Alertes système */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Alertes système</h3>
            </div>
            <div className="p-6">
              {alertsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement...</p>
                </div>
              ) : alerts?.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {alerts.map((alert, index) => (
                    <AlertItem key={index} alert={alert} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircleIcon className="h-12 w-12 mx-auto mb-2" />
                  <p>Aucune alerte active</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Métriques détaillées */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Répartition des rôles</h4>
            <div className="space-y-3">
              {stats?.roles?.map(role => (
                <div key={role.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{role.name}</span>
                  <span className="text-sm font-medium text-gray-900">{role.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Statuts des projets</h4>
            <div className="space-y-3">
              {stats?.projectStatuses?.map(status => (
                <div key={status.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{status.name}</span>
                  <span className="text-sm font-medium text-gray-900">{status.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Abonnements actifs</h4>
            <div className="space-y-3">
              {stats?.subscriptions?.map(sub => (
                <div key={sub.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{sub.name}</span>
                  <span className="text-sm font-medium text-gray-900">{sub.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;



